export const PROTOCOL_VERSION = 1 as const;

export const DATA_CHANNEL_NAMES = ["control", "clipboard", "files", "displays"] as const;

export type DataChannelName = (typeof DATA_CHANNEL_NAMES)[number];

export const DATA_CHANNEL_INIT = {
  ordered: true,
  negotiated: false,
  protocol: "",
} as const;

export type Direction = "ToDevice" | "ToOperator";

export type FileAbortReason = "rejected" | "limit" | "cancelled" | "hash-mismatch" | "error";

export type PointerAction = "move" | "down" | "up";

export type KeyAction = "down" | "up";

export type KeyModifier = "Control" | "Alt" | "Shift" | "Meta";

export type Bounds = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type Display = {
  displayId: string;
  name: string;
  primary: boolean;
  bounds: Bounds;
  scaleFactor: number;
};

export type SessionFeatures = {
  clipboard: boolean;
  fileTransfer: boolean;
};

type Envelope = {
  v: typeof PROTOCOL_VERSION;
  sessionId: string;
};

type ChannelHead = {
  v: typeof PROTOCOL_VERSION;
  id: string;
  ts: string;
};

export type SdpOffer = Envelope & {
  type: "sdp-offer";
  payload: { type: "offer"; sdp: string };
};

export type SdpAnswer = Envelope & {
  type: "sdp-answer";
  payload: { type: "answer"; sdp: string };
};

export type IceCandidate = Envelope & {
  type: "ice-candidate";
  payload: {
    candidate: string;
    sdpMid: string | null;
    sdpMLineIndex: number | null;
    usernameFragment?: string | null;
  };
};

export type DisplaysSignal = Envelope & {
  type: "displays";
  payload: { displays: Display[] };
};

export type SelectDisplay = Envelope & {
  type: "select-display";
  payload: { displayId: string };
};

export type CommandSignal = Envelope & {
  type: "command";
  payload: { nonce: string; seq: number; text: string; stop?: boolean };
};

export type CommandOutput = Envelope & {
  type: "commandOutput";
  payload: { nonce: string; seq: number; text: string; transcript?: "healthy" | "chunk" | "failed" };
};

export type QualityIcePath = "direct" | "relayed";

export type QualitySample = {
  offsetMs: number;
  fps: number;
  bitrate: number;
  rttMs: number | null;
  jitterMs: number | null;
  packetLoss: number | null;
  captureMs: number;
  encodeMs: number;
  icePath: QualityIcePath;
};

export type QualitySignal = Envelope & {
  type: "quality";
  payload: { offerLagMs?: number; samples: QualitySample[] };
};

export type SignallingEnvelope =
  | SdpOffer
  | SdpAnswer
  | IceCandidate
  | DisplaysSignal
  | SelectDisplay
  | CommandSignal
  | CommandOutput
  | QualitySignal;

export type ControlHello = ChannelHead & {
  type: "control.hello";
  features: SessionFeatures;
};

export type InputPointer = ChannelHead & {
  type: "input.pointer";
  displayId: string;
  x: number;
  y: number;
  buttons: number;
  action: PointerAction;
};

export type InputKey = ChannelHead & {
  type: "input.key";
  action: KeyAction;
  code: string;
  modifiers: KeyModifier[];
};

export type InputWheel = ChannelHead & {
  type: "input.wheel";
  displayId: string;
  deltaX: number;
  deltaY: number;
};

export type CadErrorCode = "not_connected" | "rate_limited" | "not_sent" | "not_visible" | "sas_failed";

export type InputCad = {
  type: "input.cad";
  sessionId: string;
  requestId: string;
  ts: string;
};

export type InputCadResult =
  | {
      type: "input.cad.result";
      sessionId: string;
      requestId: string;
      ok: true;
      error: null;
    }
  | {
      type: "input.cad.result";
      sessionId: string;
      requestId: string;
      ok: false;
      error: CadErrorCode;
    };

export type ClipboardSet = ChannelHead & {
  type: "clipboard.set";
  direction: Direction;
  mime: "text/plain" | "image/png";
  byteLength: number;
  payloadBase64: string;
  partIndex?: number;
  partCount?: number;
};

export type FileOffer = ChannelHead & {
  type: "file.offer";
  transferId: string;
  direction: Direction;
  name: string;
  sha256: string;
  bytes: number;
  chunkBytes: number;
};

export type FileAccept = ChannelHead & {
  type: "file.accept";
  transferId: string;
};

export type FileChunk = ChannelHead & {
  type: "file.chunk";
  transferId: string;
  index: number;
  payloadBase64: string;
};

export type FileComplete = ChannelHead & {
  type: "file.complete";
  transferId: string;
  sha256: string;
};

export type FileAck = ChannelHead & {
  type: "file.ack";
  transferId: string;
  sha256: string;
};

export type FileAbort = ChannelHead & {
  type: "file.abort";
  transferId: string;
  reason: FileAbortReason;
};

export type DisplaysAdvertise = ChannelHead & {
  type: "displays.advertise";
  displays: Display[];
};

export type DisplaysSelected = ChannelHead & {
  type: "displays.selected";
  displayId: string;
};

export type ControlMessage = ControlHello | InputPointer | InputKey | InputWheel | InputCad | InputCadResult;

export type FileMessage = FileOffer | FileAccept | FileChunk | FileComplete | FileAck | FileAbort;

export type DisplaysChannelMessage = DisplaysAdvertise | DisplaysSelected;

export type ChannelMessageMap = {
  control: ControlMessage;
  clipboard: ClipboardSet;
  files: FileMessage;
  displays: DisplaysChannelMessage;
};

export type ChannelMessage = ChannelMessageMap[DataChannelName];

export type SessionMessage = SignallingEnvelope | ChannelMessage;

export type IceServer = {
  urls: string[];
  username?: string;
  credential?: string;
};

export type ParseSuccess<T> = { ok: true; value: T };

export type ParseFailure = { ok: false; errors: string[] };

export type ParseResult<T> = ParseSuccess<T> | ParseFailure;
